import { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import ClearIcon from '@mui/icons-material/Clear'
import { format } from 'date-fns'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { StoreContext } from '../../store'
import FormLabel from '@mui/material/FormLabel'
import FormHelperText from '@mui/material/FormHelperText'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import TextField from '@mui/material/TextField'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import StyledFormControl, { classes } from './DateTimePickerStyles'

/*
 * This is custom Date Time Picker component that can be used as from control.
 * It uses components from @material-ui/pickers internally.
 * It returns object { isValid: Boolean, date: Date }
 * isValid is used because we need to tell parent component if a valid date is set.
 * It return isValid: false if one input is set but the other is not.
 * It returns date: null if both inputs are empty.
 */
const DateTimePicker = ({ onChange, label, value }) => {
  const { uiConfig } = useContext(StoreContext)
  const [error, setError] = useState(false)
  const [date, setDate] = useState(value)
  const [time, setTime] = useState(value)
  const [dateString, setDateString] = useState(format(new Date(), 'MM/dd/yyyy'))
  const [timeString, setTimeString] = useState(format(new Date(), 'hh:mm a'))

  const handleDateChange = (value) => {
    setDate(value)
    if (value) {
      const newDateString = format(value, 'MM/dd/yyyy')
      setDateString(newDateString)

      if (time === null) {
        setError(true)
        onChange({ isValid: false })
      } else {
        setError(false)
        const dateTime = format(
          newDateString + ' ' + timeString,
          'MM/dd/yyyy hh:mm a'
        )
        onChange({ isValid: true, date: dateTime })
      }
    } else {
      setDateString('')
    }
  }

  const handleTimeChange = (value) => {
    setTime(value)
    if (value) {
      const newTimeString = format(value, 'hh:mm a')
      setTimeString(newTimeString)
      if (date === null) {
        setError(true)
        onChange({ isValid: false })
      } else {
        setError(false)
        const dateTime = format(
          new Date(dateString + ' ' + newTimeString),
          'MM/dd/yyyy hh:mm a'
        )
        onChange({ isValid: true, date: dateTime })
      }
    } else {
      setDateString('')
    }
  }

  const clearDateTime = () => {
    setDate(null)
    setTime(null)
    setTimeString(null)
    setDateString(null)
    onChange({ isValid: true, date: null })
  }

  return (
    <StyledFormControl>
      <FormLabel>{label}</FormLabel>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div
          className={
            uiConfig.mobile
              ? classes.mobileDateTimePicker
              : classes.dateTimePicker
          }
        >
          <div className={classes.datePicker}>
            <MobileDatePicker
              fullWidth
              disablePast
              value={date}
              onChange={handleDateChange}
              inputFormat="MM/dd/yyyy"
              label="Date"
              renderInput={(params) => (
                <TextField variant="standard" {...params} />
              )}
            />
          </div>
          <div className={classes.timePicker}>
            <MobileTimePicker
              fullWidth
              keyboardIcon={<AccessTimeIcon />}
              value={time}
              onChange={handleTimeChange}
              mask="__:__ _M"
              label="Time"
              renderInput={(params) => (
                <TextField variant="standard" {...params} />
              )}
            />
            <ClearIcon onClick={clearDateTime} className={classes.clearIcon} />
          </div>
        </div>
      </LocalizationProvider>
      {error && (
        <FormHelperText error>Please select both date and time.</FormHelperText>
      )}
    </StyledFormControl>
  )
}

DateTimePicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
}

export default DateTimePicker
